import { FC, useMemo } from "react";
import Stack from "@mui/material/Stack";
import { TypographyWithTooltip } from "@/components/common/TypographyWithTooltip";
import { AiKubernetesRecommendationDescriptionTooltip } from "@/components/pages/ai-kubernetes-recommendations/components/AiKubernetesRecommendationDescriptionTooltip";
import { CopyIconButton } from "@/components/common/CopyIconButton";
import { useHover } from "@/utils/hooks/useHover.hook";
import { getTextFromHTML } from "@/components/utils/helpers/slack-formatters/getTextFromHTML";
import { AIIcon } from "@/assets/icons/app-related/AIIcon";

interface AiKubernetesRecommendationsDescriptionCellProps {
  description: string;
}

export const AiKubernetesRecommendationsDescriptionCell: FC<
  AiKubernetesRecommendationsDescriptionCellProps
> = ({ description }) => {
  const { ref, hovered } = useHover();
  const descriptionText = useMemo(
    () => getTextFromHTML(description),
    [description],
  );

  return (
    <Stack
      py={0.5}
      px={0.75}
      ref={ref}
      spacing={0.5}
      direction="row"
      alignItems="center"
      borderRadius={2}
      sx={{
        background: "linear-gradient(90deg, #EAEDFF 0%, #F3EDFF 100%)",
      }}
    >
      <AIIcon fontSize="small" />

      <TypographyWithTooltip
        title={
          <AiKubernetesRecommendationDescriptionTooltip
            description={description ?? ""}
          />
        }
        variant="body2"
        tooltipMaxHeight="38dvh"
        placement={"right-start"}
        tooltipProps={{
          componentsProps: customTooltipStyles,
        }}
      >
        {descriptionText}
      </TypographyWithTooltip>

      {descriptionText && (
        <CopyIconButton
          data-block-row-click
          data={descriptionText}
          visible={hovered}
          sx={{ p: 0 }}
          TooltipProps={{ placement: "top" }}
        />
      )}
    </Stack>
  );
};

const customTooltipStyles = {
  tooltip: {
    sx: {
      color: "text.primary",
      bgcolor: "background.paper",
      "& .MuiTooltip-arrow": {
        color: "background.paper",
      },
      boxShadow: 7,
      border: 1,
      borderColor: "grey.300",
      borderRadius: 3,
      p: 1,
      maxWidth: "55dvh",
      maxHeight: "40dvh",
      overflow: "hidden",
    },
  },
};
